import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import { motion } from 'framer-motion';
import { 
  AutoFixHigh,
  FormatPaint,
  Edit,
  Visibility,
  Language,
  Lightbulb,
  Assignment,
  Download,
  Verified,
} from '@mui/icons-material';

const ServicesPage = () => {
  // Tech-inspired color palette for cleverCV
  const colors = {
    primary: '#2575fc',    // Bright blue for trust and tech
    secondary: '#6a11cb',  // Deep purple for intelligence and creativity
    accent: '#37cdbe',     // Teal for freshness and innovation
    dark: '#2c3e50',       // Dark blue-gray for professionalism
    light: '#f4f5f7',      // Light gray for clean backgrounds
  };

  const features = [
    {
      icon: <AutoFixHigh sx={{ fontSize: 48 }} />,
      title: 'AI-Powered CV Analysis',
      description: 'Our advanced AI analyzes your CV and provides tailored suggestions to improve content and structure.',
      color: colors.primary,
      features: [
        'Content Optimization',
        'Keyword Suggestions',
        'Readability Enhancements',
        'Industry-Specific Advice',
      ],
    },
    {
      icon: <FormatPaint sx={{ fontSize: 48 }} />,
      title: 'Professional Templates',
      description: 'Choose from a variety of industry-specific templates designed by experts.',
      color: colors.secondary,
      features: [
        'Customizable Designs',
        'ATS-Friendly Formats',
        'Modern Aesthetics',
        'Easy to Edit',
      ],
    },
    {
      icon: <Edit sx={{ fontSize: 48 }} />,
      title: 'Real-Time Suggestions',
      description: 'Get instant feedback and AI-driven suggestions as you build your CV.',
      color: colors.accent,
      features: [
        'Live Editing',
        'Grammar & Style Checks',
        'Skill Matching',
        'Content Improvement',
      ],
    },
    {
      icon: <Visibility sx={{ fontSize: 48 }} />,
      title: 'Real-Time Preview',
      description: 'See your CV come to life instantly as you make changes.',
      color: colors.dark,
      features: [
        'Instant Updates',
        'Multiple Views',
        'Print-Ready',
        'Shareable Links',
      ],
    },
    {
      icon: <Language sx={{ fontSize: 48 }} />,
      title: 'Multi-Language Support',
      description: 'Create CVs in multiple languages to reach a global audience.',
      color: colors.primary,
      features: [
        'Language Translation',
        'Cultural Adaptation',
        'Global Formats',
        'Localized Content',
      ],
    },
    {
      icon: <Lightbulb sx={{ fontSize: 48 }} />,
      title: 'Skill Suggestions',
      description: 'Discover in-demand skills tailored to your industry and experience.',
      color: colors.secondary,
      features: [
        'Industry Trends',
        'Skill Gap Analysis',
        'Personalized Recommendations',
        'Career Growth Tips',
      ],
    },
    {
      icon: <Assignment sx={{ fontSize: 48 }} />,
      title: 'ATS Optimization',
      description: 'Ensure your CV is formatted to pass Applicant Tracking Systems with ease.',
      color: colors.accent,
      features: [
        'Keyword Optimization',
        'Formatting Checks',
        'Compatibility Assurance',
        'Success Metrics',
      ],
    },
    {
      icon: <Download sx={{ fontSize: 48 }} />,
      title: 'Multiple Export Options',
      description: 'Download your CV in various formats, including PDF, Word, and more.',
      color: colors.dark,
      features: [
        'PDF Export',
        'Word Document',
        'Plain Text',
        'Shareable Links',
      ],
    },
    {
      icon: <Verified sx={{ fontSize: 48 }} />,
      title: 'Expert-Reviewed Content',
      description: 'Get access to tips and advice from career experts to refine your CV.',
      color: colors.primary,
      features: [
        'Career Advice',
        'Content Best Practices',
        'Industry Insights',
        'Personal Branding',
      ],
    },
  ];

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* HERO SECTION */}
      <Box
        sx={{
          py: 12,
          background: `linear-gradient(135deg, ${colors.primary}dd, ${colors.secondary}dd)`,
          color: '#fff',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 3,
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              Our Features
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                maxWidth: '800px',
                mx: 'auto',
                fontWeight: 300,
              }}
            >
              Revolutionize your job search with AI-powered CV creation
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* FEATURES GRID */}
      <Box
        sx={{
          py: 12,
          backgroundColor: colors.light,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            background: `url('data:image/svg+xml,${encodeURIComponent(`
              <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 0h20v20H0z" fill="none"/>
                <path d="M10 0l10 10-10 10L0 10z" fill="#00000005"/>
              </svg>
            `)}') repeat`,
            opacity: 0.5,
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 4,
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-10px)',
                      },
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: `linear-gradient(90deg, ${feature.color}, ${colors.accent})`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Box
                          sx={{
                            color: feature.color,
                            mr: 2,
                            transform: 'scale(1.2)',
                          }}
                        >
                          {feature.icon}
                        </Box>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: colors.dark,
                          }}
                        >
                          {feature.title}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          mb: 3,
                          lineHeight: 1.7,
                        }}
                      >
                        {feature.description}
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 2,
                        }}
                      >
                        {feature.features.map((item, itemIndex) => (
                          <Typography
                            key={itemIndex}
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              display: 'flex',
                              alignItems: 'center',
                              '&::before': {
                                content: '""',
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: feature.color,
                                marginRight: 1,
                                flexShrink: 0,
                              },
                            }}
                          >
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA SECTION */}
      <Box
        sx={{
          py: 12,
          backgroundColor: colors.dark,
          color: '#fff',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }}>
            Ready to Create Your Perfect CV?
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 4,
              color: 'rgba(255,255,255,0.8)',
              fontWeight: 300,
            }}
          >
            Start building your professional CV with cleverCV today
          </Typography>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              variant="contained"
              size="large"
              href="#generate" // Replace with your actual CV creation link
              sx={{
                px: 6,
                py: 2,
                borderRadius: '30px',
                backgroundColor: colors.primary,
                fontSize: '1.1rem',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: colors.primary,
                  filter: 'brightness(110%)',
                },
              }}
            >
              Try cleverCV Now
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default ServicesPage;